import React, { useRef, useState } from 'react';

const W = 240;
const H = 180;
const PAD = 8;

const toPx = (p) => ({ x: PAD + p.x * (W - PAD * 2), y: H - PAD - p.y * (H - PAD * 2) });
const toNorm = (px, py) => ({
  x: Math.min(1, Math.max(0, (px - PAD) / (W - PAD * 2))),
  y: Math.min(1, Math.max(0, (H - PAD - py) / (H - PAD * 2))),
});

const IDENTITY = [{ x: 0, y: 0 }, { x: 1, y: 1 }];
const MID = [{ x: 0, y: 0.5 }, { x: 1, y: 0.5 }];

// Draggable curve. value = [{x,y}] | null (null == neutral).
// baseline 'diag' = tone curve (y=x neutral); 'mid' = warp curve (y=0.5 neutral,
// e.g. Hue-vs-Sat). Click empty space to add a point; double-click to remove.
export default function ToneCurve({ value, onChange, baseline = 'diag', stroke, axes, xBar }) {
  const BASE = baseline === 'mid' ? MID : IDENTITY;
  const points = value && value.length >= 2 ? value : BASE;
  const svgRef = useRef(null);
  const [drag, setDrag] = useState(-1);

  const localPos = (e) => {
    const rect = svgRef.current.getBoundingClientRect();
    const sx = W / rect.width;
    const sy = H / rect.height;
    return { px: (e.clientX - rect.left) * sx, py: (e.clientY - rect.top) * sy };
  };

  const commit = (pts) => {
    const sorted = [...pts].sort((a, b) => a.x - b.x);
    const same = sorted.length === 2
      && sorted[0].x === 0 && sorted[0].y === BASE[0].y
      && sorted[1].x === 1 && sorted[1].y === BASE[1].y;
    onChange(same ? null : sorted);
  };

  const onDown = (i) => (e) => {
    e.stopPropagation();
    setDrag(i);
  };

  const onMove = (e) => {
    if (drag < 0) return;
    const { px, py } = localPos(e);
    const n = toNorm(px, py);
    const next = points.map((p, i) => (i === drag ? { x: i === 0 ? 0 : i === points.length - 1 ? 1 : n.x, y: n.y } : p));
    commit(next);
  };

  const onUp = () => setDrag(-1);

  const onBgClick = (e) => {
    const { px, py } = localPos(e);
    const n = toNorm(px, py);
    if (n.x <= 0.02 || n.x >= 0.98) return;
    commit([...points, n]);
  };

  const removePoint = (i) => (e) => {
    e.stopPropagation();
    if (i === 0 || i === points.length - 1) return;
    commit(points.filter((_, idx) => idx !== i));
  };

  const path = points.map((p, i) => {
    const px = toPx(p);
    return `${i === 0 ? 'M' : 'L'} ${px.x.toFixed(1)} ${px.y.toFixed(1)}`;
  }).join(' ');

  const svg = (
    <svg
      ref={svgRef}
      className="cs-tonecurve"
      viewBox={`0 0 ${W} ${H}`}
      onMouseMove={onMove}
      onMouseUp={onUp}
      onMouseLeave={onUp}
      onClick={onBgClick}
    >
      <rect x="0" y="0" width={W} height={H} className="cs-tc-bg" />
      {[0.25, 0.5, 0.75].map((g) => (
        <g key={g}>
          <line x1={PAD + g * (W - PAD * 2)} y1={PAD} x2={PAD + g * (W - PAD * 2)} y2={H - PAD} className="cs-tc-grid" />
          <line x1={PAD} y1={H - PAD - g * (H - PAD * 2)} x2={W - PAD} y2={H - PAD - g * (H - PAD * 2)} className="cs-tc-grid" />
        </g>
      ))}
      {baseline === 'mid' ? (
        <line x1={PAD} y1={H - PAD - 0.5 * (H - PAD * 2)} x2={W - PAD} y2={H - PAD - 0.5 * (H - PAD * 2)} className="cs-tc-diag" />
      ) : (
        <line x1={PAD} y1={H - PAD} x2={W - PAD} y2={PAD} className="cs-tc-diag" />
      )}
      <path d={path} className="cs-tc-line" style={stroke ? { stroke } : undefined} />
      {points.map((p, i) => {
        const px = toPx(p);
        return (
          <circle
            key={i}
            cx={px.x}
            cy={px.y}
            r="6"
            className="cs-tc-pt"
            onMouseDown={onDown(i)}
            onDoubleClick={removePoint(i)}
          />
        );
      })}
    </svg>
  );

  // Bare curve (backward compatible) when no axes/legend requested.
  if (!axes && !xBar) return svg;

  // Labelled chart: y-axis ticks (top→bottom) + plot + optional colour/tone bar
  // + x-axis ticks (left→right), with optional axis titles.
  return (
    <div className="cs-tc-wrap">
      {axes?.y?.title && <div className="cs-tc-ytitle">{axes.y.title}</div>}
      {axes?.y?.ticks && (
        <div className="cs-tc-yaxis">
          {axes.y.ticks.map((t, i) => <span key={i}>{t}</span>)}
        </div>
      )}
      <div className="cs-tc-plot">
        {svg}
        {xBar && <div className="cs-tc-bar" style={{ background: xBar }} />}
      </div>
      {axes?.x?.ticks && (
        <div className="cs-tc-xaxis">
          {axes.x.ticks.map((t, i) => <span key={i}>{t}</span>)}
        </div>
      )}
      {axes?.x?.title && <div className="cs-tc-xtitle">{axes.x.title}</div>}
    </div>
  );
}
